import { useState, useEffect, useContext } from 'react'
import { AdminContext } from 'context/AdminContext'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import MediumLoadingComponent from 'components/Loading/MediumLoadingComponent'

export default function OrganizationDirectory({ embeddedInModal = false }) {
    const [organizationIdFilter, setOrganizationIdFilter] = useState('')
    const [organizationNameFilter, setOrganizationNameFilter] = useState('')
    const [adminEmailFilter, setAdminEmailFilter] = useState('')
    const [salesRepEmailFilter, setSalesRepEmailFilter] = useState('')
    const [organizations, setOrganizations] = useState([])

    const { GetOrganizationDirectoryFromBackend, SwapOrganization } = useContext(AdminContext)

    useEffect(() => {

        const abortController = new AbortController()
        
        GetOrganizationDirectoryFromBackend(organizationIdFilter, organizationNameFilter, adminEmailFilter, salesRepEmailFilter, setOrganizations, abortController)

        // This cleanup function runs when:
        // 1. The component unmounts (user navigates away)
        // 2. Before the effect runs again (new filter values)
        return () => { abortController.abort() }

    }, [organizationIdFilter, organizationNameFilter, adminEmailFilter, salesRepEmailFilter])

    const directoryContent = (
        <>
            <div className="flex rounded-md">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="number"
                        name="organizationId"
                        id="organizationId"
                        className="w-full rounded-md py-2 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400"
                        placeholder="Organization Id"
                        onChange={(event) => setOrganizationIdFilter(event.target.value)}
                    />
                </div>
            </div>
            
            <div className="flex rounded-md">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        name="organizationName"
                        id="organizationName"
                        className="w-full rounded-md py-2 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400"
                        placeholder="Organization Name"
                        onChange={(event) => setOrganizationNameFilter(event.target.value)}
                    />
                </div>
            </div>

            <div className="flex rounded-md">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        name="adminEmail"
                        id="adminEmail"
                        className="w-full rounded-md py-2 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400"
                        placeholder="Organization Owner"
                        onChange={(event) => setAdminEmailFilter(event.target.value)}
                    />
                </div>
            </div>

            <div className="flex rounded-md">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        name="salesRepEmail"
                        id="salesRepEmail"
                        className="w-full rounded-md py-2 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400"
                        placeholder="Organization Sales Rep"
                        onChange={(event) => setSalesRepEmailFilter(event.target.value)}
                    />
                </div>
            </div>

            {organizations === null && <MediumLoadingComponent />}

            {organizations !== null && (
                <>
                
                    {organizations.length === 0 && <div className="p-4 text-gray-400 text-center">No organizations found</div>}

                    {organizations.length > 0 && (
                        <div className="text-sm border border-gray-400 divide-y divide-gray-400 rounded overflow-hidden bg-white">

                            <div className='grid grid-cols-41 bg-gray-200 font-semibold divide-x divide-gray-400'>
                                <div className='col-span-1 px-2 py-1 text-center'>Id</div>
                                <div className='col-span-14 px-2 py-1'>Name</div>
                                <div className='col-span-9 px-2 py-1'>Owner</div>
                                <div className='col-span-9 px-2 py-1'>Sales Rep</div>
                                <div className='col-span-8 px-2 py-1'>Created Date</div>
                            </div>

                            {organizations.map((organization) => (
                                <div 
                                    key={organization.id} 
                                    className="grid grid-cols-41 cursor-pointer hover:bg-gray-100 divide-x divide-gray-400"
                                    onClick={() => SwapOrganization(organization, embeddedInModal)} 
                                >
                                    <div className='col-span-1 px-2 py-1 text-center flex items-center justify-center 2xl:text-base text-sm'>
                                        {organization.id}
                                    </div>

                                    <div className='col-span-14 px-2 py-1 truncate flex items-center gap-x-2'>
                                        {organization.logo ? (
                                            <img src={organization.logo} alt="Organization Logo" className="w-6 h-6 rounded-full border border-gray-200 object-cover" />
                                        ) : (
                                            <div className="w-6 h-6 rounded-full border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        )}
                                        <div className='2xl:text-base text-sm'>{organization.name}</div>
                                    </div>

                                    {organization.adminEmail && 
                                        <div className='col-span-9 px-2 py-1 truncate flex items-center 2xl:text-base text-sm'>{organization.adminEmail}</div>
                                    }
                                    {!organization.adminEmail && 
                                        <div className='col-span-9 px-2 py-1 truncate flex items-center text-gray-400 2xl:text-base text-sm'>No admin</div>
                                    }

                                    {organization.salesRepEmail && organization.salesRepEmail.length > 0 ? 
                                        <div className='col-span-9 px-2 py-1 truncate flex items-center gap-x-1 2xl:text-base text-sm'>
                                            <span className='2xl:text-sm text-xs text-gray-600 mt-0.5'>({organization.salesRepCommissionPercentage * 100}%)</span>
                                            {organization.salesRepEmail} 
                                        </div>
                                    :
                                        <div className='col-span-9 px-2 py-1 truncate flex items-center text-gray-400 2xl:text-base text-sm'>None</div>
                                    }

                                    <div className='col-span-8 px-2 py-1 truncate flex items-center gap-x-2'>
                                        <div className='2xl:text-base text-sm'>{moment.utc(organization.createdDate).local().format('MMMM DD, YYYY')}</div>
                                        <div className='2xl:text-sm text-xs text-gray-600 mt-0.5'>{moment.utc(organization.createdDate).local().format('hh:mm A')}</div>
                                    </div>

                                </div>
                            ))}

                        </div>
                    )}
                </>
            )}
        </>
    )

    if (embeddedInModal) {
        return (
            <div className="space-y-3 max-h-[calc(100vh-8rem)] overflow-y-auto text-left">
                {directoryContent}
            </div>
        )
    }

    return (
        <ScreenFrame limitWidth={false}>
            {directoryContent}
        </ScreenFrame>
    )
}
